<template>
  <div class="layout">
    <LinkButton
      tag="a"
      to="#main-content"
      class="layout__skip-link"
      secondary
      same-window
      @click="handleSkipLink"
    >
      Zum Hauptinhalt springen
    </LinkButton>

    <PageHeader
      :theme="themeHeader"
      :hide-navi="hideNavi"
    >
      <template #logo><slot name="logo" /></template>
      <template #menu><slot name="menu" /></template>
    </PageHeader>
    <client-only>
      <!-- Show popup only when nav is shown. -->
      <template v-if="!themeHeader">
        <DefaultPopup />
      </template>
    </client-only>
    <div
      id="main-content"
      class="layout__container"
      tabindex="-1"
    >
      <slot />
    </div>
    <PageFooter :theme="themeFooter" />
    <client-only>
      <LoadingSpinnerPage />
      <CustomerQueueDialog />
    </client-only>
    <div id="globals" />
    <client-only>
      <GlobalModal />
    </client-only>

    <template v-if="isDev">
      <StatusToolbarWrapper />
    </template>
  </div>
</template>

<script setup lang="ts">
import { useRuntimeConfig, defineAsyncComponent, useRoute, useRouter } from '#imports';
import { storeToRefs } from 'pinia';
import { debounce } from 'lodash-es';
import PageHeader from '~/components/page-header.vue';
import PageFooter from '~/components/page-footer.vue';
import DefaultPopup from '~/components/default-popup.vue';
import CustomerQueueDialog from '~/components/customer-queue-dialog.vue';
import { LinkButton, GlobalModal } from '@i22team/td-longtail-vue-components';
import LoadingSpinnerPage from '~/components/loading-spinner-page.vue';

import { useCartStore } from '~/stores/cart';
import { useDowntimesStore } from '~/stores/downtimes';

const config = useRuntimeConfig();

const cartStore = useCartStore();
const downtimesStore = useDowntimesStore();
const { maintenanceDowntime } = storeToRefs(downtimesStore);

function handleSkipLink() {
  setTimeout(() => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }, 10);
}

const isDev = config.public.applicationEnv !== 'production';

const StatusToolbarWrapper = isDev
  ? defineAsyncComponent(() => import('~/components/status-toolbar-wrapper.vue'))
  : null;

export type FooterProps = {
  themeHeader?: 'small' | undefined;
  themeFooter?: 'standard' | 'small';
  hideNavi?: boolean;
};

withDefaults(defineProps<FooterProps>(), {
  themeHeader: undefined,
  themeFooter: 'standard',
});

const hasWindow = () => typeof window !== 'undefined';

const route = useRoute();
const router = useRouter();

const hasVerticalScrollbar = ref(false);

const isPageWithDowntime = computed(() => {
  const routeName = route.name;
  if (typeof routeName !== 'string') return false;
  return [
    'aktionsangebote',
    'shop-warenkorb',
    'shop-kundenformular-persoenliche-daten',
    'shop-kundenformular-zahlungsart',
    'shop-zusammenfassung',
    'shop-list',
    'ListPage',
    'magenta-moments-2024',
    'ProductDetailPage',
    'magenta-tv-hardware',
    'preisvorteile-sichern',
  ].includes(routeName);
});

const isMaintenancePage = computed(() => route.name === 'maintenance');

function setViewportSize() {
  if (!hasWindow()) return;
  hasVerticalScrollbar.value = window.innerWidth > document.documentElement.clientWidth;
}

const resizeHandler = debounce(() => {
  setViewportSize();
}, 200);

async function checkForDowntime() {
  if (isMaintenancePage.value) return;
  if (!isPageWithDowntime.value) return;
  await downtimesStore.fetchDowntimes();
}

onMounted(async () => {
  if (!hasWindow()) return;

  setViewportSize();
  window.addEventListener('resize', resizeHandler);
  await checkForDowntime();

  if (!route.path?.startsWith('/shop/') && !maintenanceDowntime.value && !isMaintenancePage.value) {
    if (!config.public?.performanceMode?.enabled) {
      await cartStore.initCart();
    }
  }
});

onUnmounted(() => {
  if (!hasWindow()) return;
  window.removeEventListener('resize', resizeHandler);
});

watch(
  () => route,
  async () => {
    await checkForDowntime();
  },
  {
    deep: true,
  }
);

watch(
  maintenanceDowntime,
  async (value) => {
    if (value && isPageWithDowntime.value && !isMaintenancePage.value) {
      await router.push('/maintenance');
      // Recheck required after routing.

      await checkForDowntime();
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.layout {
  display: flex;
  flex-direction: column;
  min-height: 100vh;

  &__container {
    flex: 1;
  }

  &__skip-link {
    position: absolute;
    top: -9999px;
    left: 0;
    z-index: 100;

    &:focus {
      top: 0;
    }
  }
}
</style>
